import type { TranslateFn } from '@newo/localization';
import { phoneFieldSchema } from '@newo/ui';
import { z } from 'zod';

const emailsList = z.string().refine(
	(value) => {
		const emails = value.split(',').map((e) => e.trim());
		return emails.every((email) => z.string().email().safeParse(email).success);
	},
	{
		message: 'Invalid email.'
	}
);

interface ValidationConfig {
	isExternalAccountNumberEnabled?: boolean;
	translate: TranslateFn;
}

export const createCustomerSchema = (validationConfig: ValidationConfig) => {
	const { isExternalAccountNumberEnabled = false, translate } =
		validationConfig;

	return z.object({
		contact_email: emailsList,
		contact_fullname: z.string().trim().min(1, 'Full name is required'),
		contact_phone: phoneFieldSchema({
			required: translate('common', 'phone_required'),
			invalidMessage: translate('common', 'invalid_phone_format')
		}),
		creator_partner_idn: z.string().optional(),
		promo_code: z.string().optional(),
		account_managers: emailsList.optional().or(z.literal('')),
		country_code: z.string().optional(),
		industry: z.string(),
		partner_member_idn: z.string().optional(),
		partner_idn: z.string().optional(),
		comment: z.string().optional().or(z.literal('')),
		external_account_number: isExternalAccountNumberEnabled
			? z.string().min(1, 'External account number is required')
			: z.string().optional().or(z.literal('')),
		language: z.string().optional(),
		country: z.string().optional()
	});
};

export const createContactAgreementSchema = (translate: TranslateFn) =>
	z.object({
		phone_number: phoneFieldSchema({
			required: translate('common', 'phone_required'),
			invalidMessage: translate('common', 'invalid_phone_format')
		})
	});

export const CustomerSchema = createCustomerSchema({
	translate: (namespace, key) => `${namespace}.${key}`
});

export const isValid = <K extends keyof z.infer<typeof CustomerSchema>>(
	key: K,
	value: z.infer<typeof CustomerSchema>[K]
): boolean => {
	const schema = CustomerSchema.shape[key];
	return schema.safeParse(value).success;
};
